import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import SubscriptionStatus from '../../components/ui/SubscriptionStatus';

import AIAgentProgressTracker from '../../components/ui/AIAgentProgressTracker';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import ProjectCard from './components/ProjectCard';
import QuickActionCard from './components/QuickActionCard';
import RecentActivityItem from './components/RecentActivityItem';
import StatsCard from './components/StatsCard';
import CommunityIntegrationCard from './components/CommunityIntegrationCard';

const UserDashboard = () => {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('recent');
  const [isQuickSwitcherOpen, setIsQuickSwitcherOpen] = useState(false);
  const [recentActivities, setRecentActivities] = useState([]);
  const [stats, setStats] = useState({});
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    if (!user) {
      navigate('/user-login');
    }
  }, [user, navigate]);

  useEffect(() => {
    const mockProjects = [
      {
        id: 1,
        title: "The Entrepreneur\'s Guide to AI",
        status: 'in-progress',
        category: 'Business',
        currentStage: 'writing',
        progress: 65,
        lastModified: '2025-12-03T14:30:00',
        wordCount: 15420
      },
      {
        id: 2,
        title: 'Mastering Digital Marketing',
        status: 'draft',
        category: 'Marketing',
        currentStage: 'outline',
        progress: 30,
        lastModified: '2025-12-01T09:15:00',
        wordCount: 5200
      },
      {
        id: 3,
        title: 'Leadership in the Modern Age',
        status: 'completed',
        category: 'Leadership',
        currentStage: 'publishing',
        progress: 100,
        lastModified: '2025-11-28T16:45:00',
        wordCount: 42000
      },
      {
        id: 4,
        title: 'Building Sustainable Businesses',
        status: 'in-progress',
        category: 'Business',
        currentStage: 'formatting',
        progress: 85,
        lastModified: '2025-12-02T11:20:00',
        wordCount: 38500
      },
      {
        id: 5,
        title: 'The Art of Public Speaking',
        status: 'paused',
        category: 'Communication',
        currentStage: 'writing',
        progress: 45,
        lastModified: '2025-11-25T13:00:00',
        wordCount: 12800
      }
    ];

    const mockActivities = [
      {
        id: 1,
        type: 'project-updated',
        description: "Updated \'The Entrepreneur\'s Guide to AI\' - Chapter 5 completed",
        timestamp: '2025-12-04T18:30:00'
      },
      {
        id: 2,
        type: 'stage-completed',
        description: "Completed formatting for \'Building Sustainable Businesses'",
        timestamp: '2025-12-04T15:20:00'
      },
      {
        id: 3,
        type: 'project-created',
        description: "Started new project \'The Art of Public Speaking'",
        timestamp: '2025-12-03T10:45:00'
      },
      {
        id: 4,
        type: 'book-published',
        description: "Successfully published \'Leadership in the Modern Age\' to Amazon KDP",
        timestamp: '2025-11-28T16:45:00'
      },
      {
        id: 5,
        type: 'subscription-upgraded',
        description: 'Upgraded to Professional plan',
        timestamp: '2025-11-20T09:00:00'
      }
    ];

    const mockStats = {
      totalProjects: mockProjects?.length,
      activeProjects: mockProjects?.filter(p => p?.status === 'in-progress')?.length,
      completedBooks: mockProjects?.filter(p => p?.status === 'completed')?.length,
      totalWords: mockProjects?.reduce((sum, p) => sum + p?.wordCount, 0)
    };

    setProjects(mockProjects);
    setFilteredProjects(mockProjects);
    setRecentActivities(mockActivities);
    setStats(mockStats);
  }, []);

  // Recalculate stats whenever projects change
  useEffect(() => {
    const newStats = {
      totalProjects: projects?.length,
      activeProjects: projects?.filter(p => p?.status === 'in-progress')?.length,
      completedBooks: projects?.filter(p => p?.status === 'completed')?.length,
      totalWords: projects?.reduce((sum, p) => sum + p?.wordCount, 0)
    };
    setStats(newStats);
  }, [projects]);

  useEffect(() => {
    let filtered = [...projects];

    if (searchQuery?.trim()) {
      filtered = filtered?.filter(project =>
        project?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        project?.category?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      );
    }

    if (filterStatus !== 'all') {
      filtered = filtered?.filter(project => project?.status === filterStatus);
    }

    if (sortBy === 'recent') {
      filtered?.sort((a, b) => new Date(b.lastModified) - new Date(a.lastModified));
    } else if (sortBy === 'title') {
      filtered?.sort((a, b) => a?.title?.localeCompare(b?.title));
    } else if (sortBy === 'progress') {
      filtered?.sort((a, b) => b?.progress - a?.progress);
    }

    setFilteredProjects(filtered);
  }, [searchQuery, filterStatus, sortBy, projects]);

  const handleStartNewProject = () => {
    navigate('/ai-agent-workflow-hub');
  };

  const handleDeleteProject = (projectId) => {
    // Remove project from both projects and filteredProjects
    setProjects(prevProjects => prevProjects?.filter(p => p?.id !== projectId));
    setFilteredProjects(prevFiltered => prevFiltered?.filter(p => p?.id !== projectId));
    
    // Add activity for deletion
    const deletedProject = projects?.find(p => p?.id === projectId);
    if (deletedProject) {
      const newActivity = {
        id: Date.now(),
        type: 'project-deleted',
        description: `Deleted project '${deletedProject?.title}'`,
        timestamp: new Date()?.toISOString()
      };
      setRecentActivities(prev => [newActivity, ...prev]);
    }
  };

  const handleKeyPress = (e) => {
    if ((e?.ctrlKey || e?.metaKey) && e?.key === 'k') {
      e?.preventDefault();
      setIsQuickSwitcherOpen(true);
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, []);

  const statusOptions = [
    { value: 'all', label: 'All Projects' },
    { value: 'in-progress', label: 'In Progress' },
    { value: 'draft', label: 'Draft' },
    { value: 'completed', label: 'Completed' },
    { value: 'paused', label: 'Paused' }
  ];

  const sortOptions = [
    { value: 'recent', label: 'Recently Modified' },
    { value: 'title', label: 'Title (A-Z)' },
    { value: 'progress', label: 'Progress' }
  ];

  return (
    <>
      <Helmet>
        <title>Dashboard - AuthorPreneur AI</title>
        <meta name="description" content="Manage your book projects, track progress, and access AI-powered writing tools in your AuthorPreneur AI dashboard." />
      </Helmet>
      <div className="min-h-screen bg-background">
        <main className="pt-20 pb-12">
          <div className="container mx-auto px-4">
            <div className="mb-8">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
                <div>
                  <h1 className="text-3xl font-heading font-bold text-foreground mb-2">
                    Welcome Back!
                  </h1>
                  <p className="text-muted-foreground">
                    Continue your book creation journey and manage your projects
                  </p>
                </div>
                <div className="flex items-center space-x-3">
                  <Button
                    variant="outline"
                    size="default"
                    iconName="Command"
                    iconPosition="left"
                    onClick={() => setIsQuickSwitcherOpen(true)}
                    className="hidden lg:flex"
                  >
                    Quick Switch
                  </Button>
                  <Button
                    variant="default"
                    size="default"
                    iconName="Plus"
                    iconPosition="left"
                    onClick={handleStartNewProject}
                  >
                    New Project
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatsCard
                  title="Total Projects"
                  value={stats?.totalProjects}
                  icon="FolderOpen"
                  iconColor="bg-primary"
                  trend="up"
                  trendValue="+2"
                />
                <StatsCard
                  title="Active Projects"
                  value={stats?.activeProjects}
                  icon="Clock"
                  iconColor="bg-secondary"
                  trend={null}
                  trendValue={null}
                />
                <StatsCard
                  title="Completed Books"
                  value={stats?.completedBooks}
                  icon="CheckCircle"
                  iconColor="bg-success"
                  trend="up"
                  trendValue="+1"
                />
                <StatsCard
                  title="Total Words"
                  value={stats?.totalWords?.toLocaleString()}
                  icon="FileText"
                  iconColor="bg-accent"
                  trend={null}
                  trendValue={null}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-6">
                <div className="bg-card border border-border rounded-lg shadow-sm p-6">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                    <h2 className="text-xl font-heading font-semibold text-foreground">
                      Your Projects
                    </h2>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <Input
                        type="search"
                        placeholder="Search projects..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e?.target?.value)}
                        className="sm:w-64"
                      />
                      <div className="flex gap-2">
                        <Select
                          options={statusOptions}
                          value={filterStatus}
                          onChange={setFilterStatus}
                          className="flex-1 sm:w-40"
                        />
                        <Select
                          options={sortOptions}
                          value={sortBy}
                          onChange={setSortBy}
                          className="flex-1 sm:w-40"
                        />
                      </div>
                    </div>
                  </div>

                  {filteredProjects?.length === 0 ? (
                    <div className="text-center py-12">
                      <Icon name="Search" size={48} className="mx-auto mb-4 text-muted-foreground opacity-50" />
                      <p className="text-muted-foreground mb-4">
                        {searchQuery ? 'No projects found matching your search' : 'No projects yet'}
                      </p>
                      {!searchQuery && (
                        <Button
                          variant="default"
                          size="default"
                          iconName="Plus"
                          iconPosition="left"
                          onClick={handleStartNewProject}
                        >
                          Create Your First Project
                        </Button>
                      )}
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 gap-4">
                      {filteredProjects?.map(project => (
                        <ProjectCard 
                          key={project?.id} 
                          project={project}
                          onDelete={handleDeleteProject}
                        />
                      ))}
                    </div>
                  )}
                </div>

                <div className="bg-card border border-border rounded-lg shadow-sm p-6">
                  <h2 className="text-xl font-heading font-semibold text-foreground mb-4">
                    Quick Actions
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <QuickActionCard
                      title="Start New Book"
                      description="Begin your book creation journey with AI-powered topic research"
                      icon="BookOpen"
                      iconColor="bg-primary"
                      onClick={handleStartNewProject}
                    />
                    <QuickActionCard
                      title="View All Projects"
                      description="Browse and manage all your book projects in one place"
                      icon="FolderOpen"
                      iconColor="bg-secondary"
                      onClick={() => navigate('/book-project-details')}
                    />
                    <QuickActionCard
                      title="Manage Subscription"
                      description="View your plan details and upgrade for more features"
                      icon="CreditCard"
                      iconColor="bg-accent"
                      onClick={() => navigate('/subscription-management')}
                    />
                    <QuickActionCard
                      title="AI Workflow Hub"
                      description="Access all AI agents and tools for book creation"
                      icon="Zap"
                      iconColor="bg-success"
                      onClick={() => navigate('/ai-agent-workflow-hub')}
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                <SubscriptionStatus />

                <AIAgentProgressTracker />

                <CommunityIntegrationCard
                  isConnected={true}
                  memberCount={1247}
                  lastActivity="2h ago"
                />

                <div className="bg-card border border-border rounded-lg shadow-sm p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-base font-heading font-semibold text-foreground">
                      Recent Activity
                    </h3>
                    <button
                      className="text-xs text-primary hover:text-primary/80 transition-colors duration-200"
                      onClick={() => {}}
                    >
                      View All
                    </button>
                  </div>
                  <div className="space-y-0">
                    {recentActivities?.slice(0, 5)?.map(activity => (
                      <RecentActivityItem key={activity?.id} activity={activity} />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default UserDashboard;